// Webrtc plugin.
import * as mediaSagas from './media'
import * as logSagas from './logs'
import * as actionTypes from '../interface/actionTypes'

// Other plugins.
import { HANDLERS_CHANGE, LEVELS_CHANGE } from '../../logs/interface/actionTypes'
import { getLevel } from '../../logs/interface/selectors'

// Libraries.
import { call, select, takeEvery } from 'redux-saga/effects'

/**
 * Track operations.
 */

/**
 * Handle rendering Track objects.
 * @method renderTracks
 * @param  {Object} webRTC The webRTC stack.
 */
export function * renderTracks (webRTC) {
  yield takeEvery(actionTypes.RENDER_TRACKS, mediaSagas.renderTracks, webRTC)
}

/**
 * Handle removing Track objects.
 * @method removeTracks
 * @param  {Object} webRTC The webRTC stack.
 */
export function * removeTracks (webRTC) {
  yield takeEvery(actionTypes.REMOVE_TRACKS, mediaSagas.removeTracks, webRTC)
}

/**
 * Handle muting tracks.
 * @method muteTracks
 * @param  {Object} webRTC The webRTC stack.
 */
export function * muteTracks (webRTC) {
  yield takeEvery(actionTypes.MUTE_TRACKS, mediaSagas.muteTracks, webRTC)
}

/**
 * Handle unmuting tracks.
 * @method unmuteTracks
 * @param  {Object} webRTC The webRTC stack.
 */
export function * unmuteTracks (webRTC) {
  yield takeEvery(actionTypes.UNMUTE_TRACKS, mediaSagas.unmuteTracks, webRTC)
}

/**
 * Handle initialization of device(s).
 * @method initializeDevices
 * @param  {Object} webRTC The webRTC stack.
 */
export function * initializeDevices (webRTC) {
  yield takeEvery(actionTypes.INITIALIZE_DEVICES, mediaSagas.initializeDevices, webRTC)
}

/**
 * Taker saga for "change webrtc log level" actions.
 * @method updateLogLevelEntry
 * @param  {Object} webRTC The webRTC stack.
 */
export function * updateLogLevelEntry (webRTC) {
  yield takeEvery(LEVELS_CHANGE, logSagas.updateLogLevel, webRTC)
}

/**
 * Taker saga for "change webrtc log handler" actions.
 * @method updateLogHandlerEntry
 * @param  {Object} webRTC The webRTC stack.
 */
export function * updateLogHandlerEntry (webRTC) {
  yield takeEvery(HANDLERS_CHANGE, logSagas.updateLogHandler, webRTC)
}

/**
 * Configure the WebRTC stack's log level on SDK initialization.
 *
 * Special-case saga: This functionality needs to be run when the SDK is
 *    initialized, but can't be part of the plugin's `init` saga. This is
 *    because it needs the level state to have been set in state, but that isn't
 *    guaranteed during the `init` saga. The order that plugins are loaded would
 *    affect this (logs would need to be before webrtc).
 * @method initLogLevel
 * @param  {Object} webRTC The webRTC stack.
 */
export function * initLogLevel (webRTC) {
  // Get the WEBRTC logger's level.
  const level = yield select(getLevel, 'WEBRTC')
  // Call the "set log level" saga for the WEBRTC logger, pretending that we
  //    received an action updating its level.
  yield call(logSagas.updateLogLevel, webRTC, { payload: { WEBRTC: level } })
}
